import React, {FC, Key, memo, useCallback,  useState} from "react";
import {ColumnProps, TablePaginationConfig, TableProps} from "antd/lib/table";
import {Table} from "antd";
import {Moment} from "moment";
import classNames from "classnames";
import {FormikProps} from "formik";
import {orange, red} from "@ant-design/colors";
import css from "./LessonBookings.less";
import {
    ArrayDataOptions,
    CourseLessonBooking,
    GetBookingsByCourseIdWithCourseLessonBookingsQuery,
    GetHolidaysByCompanyAndDateRangeQuery,
    Maybe,
    UpdateCourseAndBookingsInput,
} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {renderDateAsWeekdayTag, renderDateFormatted} from "../../../views/TableColumnRenderers/TableColumnRenderers";
import {getCanceledLessons, getNewLessons, renderIndex} from "./helper";
import {LessonOfTheCourseType, PreviewBookingType} from "./PreviewBooking";
import Button from "../../../views/Button/Button";
import DateRangePicker from "./DateRangePicker";
import {defaultPageSize} from "./ModalContainer";
import {getPagination} from "../../../helpers/getPagination";

type LessonBookingCourseLessonTableData = GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"][number]["courseLessons"][number] & {
    localKey: string;
};

type LessonTableInteractiveProps = {
    currentBooking: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"][0];
    booking: PreviewBookingType;
    lessonsOfCourse: LessonOfTheCourseType;
    formikProps: FormikProps<UpdateCourseAndBookingsInput>;
    getHolidayInfo: (date: Moment) => GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number] | undefined;
    courseLessonBookingsPreview?: Array<CourseLessonBooking>;
    editBookedLessons: boolean;
    setEditBookedLessons: (editBookedLessons: boolean) => void;
    pagination?: TableProps<LessonBookingCourseLessonTableData>["pagination"];
    options?: Maybe<ArrayDataOptions>;
    setOptions: (options: ArrayDataOptions) => void;
    holidays: Array<GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number]>;
    disabled?: boolean;
};

const LessonTableInteractive: FC<LessonTableInteractiveProps> = ({
    currentBooking,
    booking,
    lessonsOfCourse,
    getHolidayInfo,
    formikProps,
    courseLessonBookingsPreview,
    editBookedLessons,
    setEditBookedLessons,
    pagination,
    options,
    setOptions,
    holidays,
    disabled = false,
}) => {
    const [selectedLessonIds, setSelectedLessonIds] = useState<Array<React.Key>>([]);
    const [showPicker, setShowPicker] = useState<boolean>(false);

    const initialBookedLessons = currentBooking.courseLessonBookings ?? [];
    const initialBookedLessonsIds = initialBookedLessons.map((lesson) => lesson.courseLessonId);
    const newLessons = getNewLessons(initialBookedLessonsIds, selectedLessonIds);
    const canceledLessons = getCanceledLessons(initialBookedLessonsIds, selectedLessonIds);

    // console.group("LessonTableInteractive");
    // console.log("initialBookedLessons", initialBookedLessons);
    // console.log("selectedLessonIds", selectedLessonIds);
    // console.log("newLessons", newLessons);
    // console.log("canceledLessons", canceledLessons);
    // console.groupEnd();

    const updateCourseLessonBookingsOnCurrentBooking = useCallback(
        (selectedRowKeys: Array<React.Key>, save: boolean) => {
            const {values} = formikProps;
            const currentBookingId = booking.id;
            const currentUpdatedBookingsData = values.updatedBookingsData ?? [];
            const updatedBookingsData = currentUpdatedBookingsData.map((booking) => {
                if (booking.id === currentBookingId) {
                    return {
                        ...booking,
                        courseLessonIds: selectedRowKeys,
                    };
                }

                return booking;
            });

            setSelectedLessonIds(selectedRowKeys);
            if (save) {
                formikProps.setFieldValue("updatedBookingsData", updatedBookingsData);
                setEditBookedLessons(false);
            }
        },
        [formikProps, booking.id, setEditBookedLessons],
    );

    // Making sure that the editBookedLessons is false when the booking changes
    React.useEffect(() => {
        setEditBookedLessons(false);
    }, [setEditBookedLessons, booking.id]);

    const onCancel = useCallback(() => {
        const currentUpdatedBookingsData = formikProps.values.updatedBookingsData ?? [];
        const currentBookingId = booking.id;
        const updatedBookingsData = currentUpdatedBookingsData.map((booking) => {
            if (booking.id === currentBookingId) {
                return booking;
            }

            return booking;
        });

        formikProps.setFieldValue("updatedBookingsData", updatedBookingsData);
        setEditBookedLessons(false);
    }, [formikProps, setEditBookedLessons, booking.id]);

    const columns: Array<ColumnProps<LessonBookingCourseLessonTableData>> = [
        {
            title: "#",
            dataIndex: "number",
            align: "center",
            render: (text, record, rowIndex) => renderIndex(record?.indexOfLessonInCourse ?? rowIndex, getHolidayInfo(record.startDateTime)),
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.weekday(),
            dataIndex: "dayOfTheWeek",
            render: (text, record) =>
                renderDateAsWeekdayTag({
                    value: record.startDateTime,
                    style: {
                        width: "100%",
                        textAlign: "center",
                        fontWeight: 600,
                    },
                }),
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.date(),
            dataIndex: "startDateTime",
            render: (text, record) => renderDateFormatted(record.startDateTime, "DD. MMM YYYY"),
        },
    ];

    React.useEffect(() => {
        // Im Edit-Modus nicht überschreiben — die User-Auswahl wird erst bei "Übernehmen" persistiert
        if (editBookedLessons) return;
        if (!courseLessonBookingsPreview) return;
        const newIds = courseLessonBookingsPreview.map((courseLessonBooking) => courseLessonBooking.courseLessonId);
        const sameContent = newIds.length === selectedLessonIds.length && newIds.every((id) => selectedLessonIds.includes(id));
        if (!sameContent) {
            setSelectedLessonIds(newIds);
        }
        // selectedLessonIds bewusst NICHT in deps: nur Lesen für Vergleich, Update wäre Endlos-Loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseLessonBookingsPreview, editBookedLessons]);

    const onChange: (pagination: TablePaginationConfig) => void = (pagination) => {
        const updatedOptions = {
            ...options,
            limit: getPagination({
                pagination,
                defaultSize: defaultPageSize,
            }).limit,
            offset: getPagination({
                pagination,
                defaultSize: defaultPageSize,
            }).offset,
        };

        setOptions(updatedOptions);
    };

    const renderTable = () => {
        return (
            <Table<LessonBookingCourseLessonTableData>
                className="lessonSelectionTable"
                rowKey="id"
                size="small"
                columns={columns}
                // dataSource={lessonsOfTheBooking}
                dataSource={lessonsOfCourse as any}
                rowSelection={{
                    type: "checkbox",
                    hideSelectAll: true,
                    selectedRowKeys: selectedLessonIds,
                    onChange: (selectedRowKeys) => {
                        if (!disabled) {
                            updateCourseLessonBookingsOnCurrentBooking(selectedRowKeys, !editBookedLessons);
                        }
                    },
                    getCheckboxProps: () => ({
                        disabled,
                    }),
                }}
                rowClassName={(record) => {
                    if (newLessons.includes(record.id)) {
                        return classNames(css.newBooking, css.bookedLesson, css.extraSmallRow);
                    }
                    if (canceledLessons.includes(record.id)) {
                        return classNames(css.canceledBooking, css.unbookedLesson, css.extraSmallRow);
                    }
                    if (selectedLessonIds.includes(record.id)) {
                        return classNames(css.extraSmallRow, css.bookedLesson);
                    }

                    return classNames(css.extraSmallRow, css.unbookedLesson);
                }}
                pagination={
                    pagination ?? {
                        current: options?.offset && options?.limit ? options.offset / options.limit + 1 : 1,
                        pageSize: options?.limit ?? defaultPageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "15", "20", "25", "50", "100"],
                        defaultPageSize,
                        total: lessonsOfCourse?.length ?? 0,
                    }
                }
                onChange={onChange}
            />
        );
    };

    const halfSpace = 3;

    if (editBookedLessons) {
        return (
            <div className={css.activeEditMultipleLessons} /* style={{boxShadow: "11px 11px 11px 11px", border: `2px solid ${orange[4]}`}} */>
                <DateRangePicker
                    selectedLessonIds={selectedLessonIds}
                    setSelectedLessonIds={setSelectedLessonIds}
                    lessonsOfCourse={lessonsOfCourse}
                    getHolidayInfo={getHolidayInfo}
                    showPicker={showPicker}
                    setShowPicker={setShowPicker}
                    holidays={holidays}
                />
                {renderTable()}
                <div style={{height: "32px"}}>
                    <Button style={{width: `calc(33% - ${halfSpace}px)`, border: "none", color: red[7], fontWeight: 600}} onClick={onCancel} disabled={disabled}>
                        {"Abbrechen"}
                    </Button>
                    <Button
                        /* size="small" */
                        style={{
                            width: `calc(67% - ${halfSpace}px)`,
                            backgroundColor: orange[4],
                            borderColor: orange[4],
                            color: "white",
                            marginLeft: `calc(${halfSpace}px * 2)`,
                            borderRadius: 0,
                            borderTopLeftRadius: 5,
                        }}
                        onClick={() => {
                            if (!disabled) {
                                updateCourseLessonBookingsOnCurrentBooking(selectedLessonIds, true);
                            }
                        }}
                        disabled={disabled}
                    >
                        {"Übernehmen"}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Button
                block
                // size="small"
                onClick={() => {
                    if (!disabled) {
                        setEditBookedLessons(true);
                    }
                }}
                disabled={disabled}
                style={{backgroundColor: orange[4], color: "white", marginBottom: "8px"}}
            >
                {"Mehrere Einheiten bearbeiten"}
            </Button>
            {renderTable()}
        </>
    );
};

export default memo(LessonTableInteractive);
